/**
 * Hand-rolled PUBLIC provenance client.
 *
 * Only the UNAUTHENTICATED public read survives here — it stays hand-rolled on
 * purpose. The orval-generated `tracePublicControllerGetProvenance` routes
 * through the `fseAxios` mutator, which attaches a Bearer token from
 * localStorage; that is WRONG for the public QR-verification viewer
 * (`verify.$kind.$id.tsx`), which runs logged-out. So this bare `fetch()` (no
 * Authorization header) is kept as the dedicated public-page transport.
 *
 * The authed `createTraceEvent` POST that used to live here was repointed to the
 * generated `useTraceControllerRecordEvent` hook (TD-103).
 *
 * Backend contract:
 *  - GET /trace/subject/:kind/:id   PUBLIC (no auth) → PublicProvenanceDto
 */

const DEFAULT_BASE_URL = 'https://api.foodsecurityexchange.cloud';

function resolveBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_FSE_API_URL;
  if (typeof fromEnv === 'string' && fromEnv.length > 0) return fromEnv;
  return DEFAULT_BASE_URL;
}

// ── DTO contract (mirrors the backend public-read shape) ────────────────────

/** Subject kind a trace event can be attached to. */
export type TraceSubjectKind = 'batch' | 'contribution' | 'transaction';

/** On-chain anchor lifecycle for a single event. */
export type TraceAnchorStatus = 'anchored' | 'pending';

/** One event in a subject's public provenance trail. */
export interface PublicProvenanceEventDto {
  eventKind: string;
  occurredAt: string;
  /** keccak256 content hash, hex (0x-prefixed). */
  contentHashHex: string;
  /** True when the stored payload re-hashes to `contentHashHex` (tamper-evident). */
  contentHashValid: boolean;
  anchorStatus: TraceAnchorStatus;
  /** On-chain tx hash once anchored, else null. */
  chainTxHashHex: string | null;
  /** Redacted, whitelisted payload safe for public display. */
  redactedPayload: Record<string, unknown>;
}

/** Roll-up verification summary across all events for a subject. */
export interface ProvenanceVerificationDto {
  allValid: boolean;
  anchoredCount: number;
  pendingCount: number;
}

/** Full public provenance read for a subject. */
export interface PublicProvenanceDto {
  subjectKind: TraceSubjectKind;
  subjectId: string;
  events: PublicProvenanceEventDto[];
  verification: ProvenanceVerificationDto;
}

/** Best-effort extraction of a server error message for thrown errors. */
async function describeError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { message?: unknown };
    if (body && typeof body === 'object' && 'message' in body) {
      return String(body.message);
    }
  } catch {
    // Non-JSON body (e.g. an HTML error page) — fall through to status text.
  }
  return `${res.status} ${res.statusText}`;
}

/**
 * Fetch a subject's public provenance trail. PUBLIC — sends NO Authorization
 * header (the viewer runs logged-out). A 400 (bad kind/uuid) or 404 surfaces
 * as a thrown Error carrying the server message.
 *
 * Kept hand-rolled (not orval) precisely because the generated client attaches
 * a Bearer token via the fseAxios mutator — wrong for an unauthenticated page.
 */
export async function getPublicProvenance(
  kind: string,
  id: string,
): Promise<PublicProvenanceDto> {
  const res = await fetch(
    `${resolveBaseUrl()}/trace/subject/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
  );
  if (!res.ok) throw new Error(await describeError(res));
  return (await res.json()) as PublicProvenanceDto;
}
